import { useEffect, useState } from "react";
import { BrandIcon } from "../../components/BrandIcon";
import { ConversationSpend } from "../../components/ConversationSpend";
import type { ConversationDTO, UserDTO } from "../../../shared/types";
import { List, ListRow } from "../../components/List";
import { Tabs } from "../../components/Tabs";
import { TextLink } from "../../components/TextLink";
import { Transcript } from "../../components/Transcript";
import { Badge, PageHeader, timeAgo } from "../../components/ui";
import { get } from "../../lib/api";
import { useConversation } from "../../lib/useConversation";
import { realtime } from "../../lib/ws";

type SourceFilter = "" | "web" | "slack";

export function AdminConversations({ conversationId }: { conversationId?: string }) {
  const [conversations, setConversations] = useState<ConversationDTO[]>([]);
  const [users, setUsers] = useState<UserDTO[]>([]);
  const [source, setSource] = useState<SourceFilter>("");
  const [q, setQ] = useState("");
  const state = useConversation(conversationId);

  const load = () => get<ConversationDTO[]>(`/api/conversations?all=1${source ? `&source=${source}` : ""}`).then(setConversations, () => {});

  useEffect(() => {
    get<UserDTO[]>("/api/users").then(setUsers, () => {});
  }, []);
  useEffect(() => {
    load();
    // New conversations (web or Slack) show up live.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const off = realtime.subscribe("admin", (e) => {
      if (e.type !== "message.created") return;
      clearTimeout(timer);
      timer = setTimeout(load, 500);
    });
    return () => {
      off();
      clearTimeout(timer);
    };
  }, [source]);

  const userName = (id: string) => users.find((u) => u.id === id)?.name ?? id.slice(0, 8);
  const needle = q.trim().toLowerCase();
  const visible = conversations.filter((c) => !needle || c.title.toLowerCase().includes(needle) || userName(c.userId).toLowerCase().includes(needle));
  const current = conversations.find((c) => c.id === conversationId);

  return (
    <div className="flex h-full min-h-0">
      {/* On phones: list OR transcript; from md: both side by side. */}
      <div className={`scroll-thin w-full shrink-0 overflow-auto md:block md:w-80 ${conversationId ? "hidden" : "block"}`}>
        <PageHeader title="Conversations" subtitle="All chats from the web app and Slack, all users. Live." />
        <div className="space-y-2 px-4 pb-4 md:px-4">
          <Tabs
            size="sm"
            label="Source"
            tabs={[{ key: "all", label: "All" }, { key: "web", label: "Web" }, { key: "slack", label: "Slack" }]}
            value={source || "all"}
            onChange={(key) => setSource(key === "all" ? "" : (key as SourceFilter))}
          />
          <input
            className="w-full rounded-md border border-line bg-surface px-3 py-1.5 text-sm outline-none focus:border-muted"
            placeholder="Search title or user"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <List empty="No conversations.">
            {visible.map((c) => (
              <ListRow
                key={c.id}
                to={`/admin/conversations/${c.id}`}
                className={c.id === conversationId ? "bg-fill" : ""}
                title={
                  <>
                    <span className="min-w-0 truncate">{c.title}</span>
                    {c.source === "slack" && <Badge tone="blue">Slack</Badge>}
                  </>
                }
                subtitle={`${userName(c.userId)} · ${timeAgo(c.updatedAt)}`}
              />
            ))}
          </List>
        </div>
      </div>
      <div className={`scroll-thin min-w-0 flex-1 overflow-auto ${conversationId ? "block" : "hidden md:block"}`}>
        {conversationId && (
          <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 border-b border-line bg-surface/90 px-4 py-2 backdrop-blur">
            <TextLink to="/admin/conversations" quiet className="text-sm md:hidden">← All</TextLink>
            {current && (
              <span className="flex min-w-0 items-center gap-2 text-sm">
                <span className="truncate font-medium">{current.title}</span>
                {current.source === "slack" ? <Badge tone="blue"><BrandIcon brand="slack" className="mr-1 size-3" />Slack · {current.slackChannel}</Badge> : <Badge>Web</Badge>}
                <span className="hidden text-xs text-faint sm:inline">{userName(current.userId)}</span>
              </span>
            )}
            <span className="w-full md:ml-auto md:w-auto">
              <ConversationSpend conversationId={conversationId} label="Last 24 h" />
            </span>
          </div>
        )}
        <div className="min-w-0 px-4 py-4 md:px-6">
          {!conversationId ? (
            <p className="card p-8 text-sm text-muted">Select a conversation to view its live transcript.</p>
          ) : state.loading ? (
            <p className="card p-8 text-sm text-muted">Loading…</p>
          ) : state.error ? (
            <p className="card p-8 text-sm text-danger">{state.error}</p>
          ) : (
            <div className="overflow-hidden card">
              <Transcript state={state} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
